import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardHeader,
  Input,
} from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import { routes } from '../../lib/routes';
import {
  useCreateRoutingRule,
  useOrgIntegration,
} from '../../hooks/useOrgIntegrations';
import { useProviders } from '../../hooks/useIntegrations';
import { useMeSpaces } from '../../hooks/useMeSpaces';
import PatternBuilder from './PatternBuilder';
import BackLink from '../../components/navigation/BackLink';
import { parsePattern, validateRow } from './pattern-helpers';
import { isSlackIntegration } from '../../lib/slack-integration';
import {
  normalizeFilterFields,
  type RoutingPattern,
} from '../../types/integrations';

export default function RoutingRuleForm() {
  const { integrationId } = useParams<{ integrationId: string }>();
  const navigate = useNavigate();
  const { success, error: showError } = useToast();

  const { data: integration } = useOrgIntegration(integrationId);
  const { data: providers } = useProviders();
  const { data: spaces } = useMeSpaces();
  const createRule = useCreateRoutingRule(integrationId);

  const provider = providers?.find((p) => p.name === integration?.provider);
  // Normalize once: tolerate either rich objects or bare-string paths.
  const fields = useMemo(
    () => normalizeFilterFields(provider?.filterFields),
    [provider?.filterFields],
  );
  const supportedEventTypes = provider?.supportedEventTypes ?? [];

  // Slack DMs can route to the sender's own personal space, resolved at event
  // time. Only offer that target type for Slack integrations.
  const allowDynamicTarget = isSlackIntegration(integration ?? {});
  const [targetType, setTargetType] = useState<'space' | 'dynamic'>('space');
  const [targetSpaceId, setTargetSpaceId] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState(100);
  const [status, setStatus] = useState<'active' | 'paused'>('active');
  const [pattern, setPattern] = useState<RoutingPattern>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  const targetSpace = useMemo(
    () => spaces?.items.find((s) => s.spaceId === targetSpaceId),
    [spaces, targetSpaceId],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!integrationId) return;
    const isDynamic = allowDynamicTarget && targetType === 'dynamic';
    if (!isDynamic && !targetSpaceId) {
      setFormError('Pick a target space');
      return;
    }

    // Re-parse the pattern back into rows so we can validate the row inputs.
    const { rows: parsedRows } = parsePattern(pattern, fields);
    const nextRowErrors: Record<string, string> = {};
    for (const row of parsedRows) {
      const err = validateRow(row, fields);
      if (err) nextRowErrors[row.id] = err;
    }
    setRowErrors(nextRowErrors);
    if (Object.keys(nextRowErrors).length > 0) {
      setFormError('Some filters need a value.');
      return;
    }

    try {
      await createRule.mutateAsync({
        ...(isDynamic
          ? { dynamicTarget: { kind: 'sender-personal-space' as const } }
          : { targetSpaceId }),
        description: description.trim() || undefined,
        priority,
        status,
        pattern,
      });
      success('Routing rule created');
      navigate(routes.console.org.integration(integrationId));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create rule');
    }
  };

  if (!integrationId) return null;

  return (
    <div className="space-y-6 max-w-3xl">
      <BackLink to={routes.console.org.integration(integrationId)}>
        {integration?.displayName ?? 'Integration'}
      </BackLink>
      <div>
        <h1 className="text-2xl font-bold text-foreground mt-1">
          New routing rule
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Forward events from {integration?.displayName ?? 'this integration'}{' '}
          into a space when they match the pattern below.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader title="Target" />
          <div className="space-y-4">
            {allowDynamicTarget && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Route to
                </label>
                <div className="inline-flex rounded-md border border-input p-0.5 text-sm">
                  {(
                    [
                      ['space', 'A space'],
                      ['dynamic', "DM sender's personal space"],
                    ] as const
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setTargetType(value)}
                      className={`rounded px-3 py-1.5 font-medium transition-colors ${
                        targetType === value
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {allowDynamicTarget && targetType === 'dynamic' ? (
              <p className="text-sm text-muted-foreground">
                Events route to the personal space of whoever sent the DM,
                resolved at delivery time.
              </p>
            ) : (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Target space
                </label>
                <select
                  value={targetSpaceId}
                  onChange={(e) => setTargetSpaceId(e.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  required
                >
                  <option value="">Pick a space…</option>
                  {(spaces?.items ?? []).map((s) => (
                    <option key={s.spaceId} value={s.spaceId}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {targetSpace && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Matching events will be forwarded to {targetSpace.name}.
                  </p>
                )}
              </div>
            )}

            <Input
              label="Description (optional)"
              value={description}
              onChange={(e: { target: { value: string } }) =>
                setDescription(e.target.value)
              }
              placeholder="e.g. Forward Jira project PROJ-A to Engineering"
            />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Priority
                </label>
                <Input
                  type="number"
                  value={String(priority)}
                  onChange={(e: { target: { value: string } }) =>
                    setPriority(Number(e.target.value))
                  }
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Lower numbers run first. Default: 100.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Status
                </label>
                <div className="inline-flex rounded-md border overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setStatus('active')}
                    className={`px-3 py-2 text-sm ${
                      status === 'active'
                        ? 'bg-primary-600 text-white'
                        : 'bg-background hover:bg-muted'
                    }`}
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatus('paused')}
                    className={`px-3 py-2 text-sm ${
                      status === 'paused'
                        ? 'bg-primary-600 text-white'
                        : 'bg-background hover:bg-muted'
                    }`}
                  >
                    Paused
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Match pattern"
            description="Pin to specific event types and add field filters. Events must match every condition to be forwarded."
          />
          <PatternBuilder
            fields={fields}
            supportedEventTypes={supportedEventTypes}
            value={pattern}
            onChange={setPattern}
            rowErrors={rowErrors}
          />
        </Card>

        {formError && (
          <p className="text-sm text-destructive">{formError}</p>
        )}

        <div className="flex justify-end gap-3">
          <Link to={routes.console.org.integration(integrationId)}>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Link>
          <Button
            type="submit"
            isLoading={createRule.isPending}
            disabled={!targetSpaceId}
          >
            Create rule
          </Button>
        </div>
      </form>
    </div>
  );
}
